import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Instagram, Lock } from "lucide-react";
import Logo from "@/components/portal/Logo";

// Official WhatsApp brand glyph (single-color, sizes with currentColor)
const WhatsAppIcon = ({ size = 15, className = "" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="currentColor"
    aria-hidden="true"
    className={className}
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.297-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347M12.05 21.785h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884M20.463 3.488A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413" />
  </svg>
);

const WHATSAPP_NUMBER = "+91 8433910466";
const WHATSAPP_LINK = "https://wa.me/918433910466";
const INSTAGRAM_HANDLE = "tanish_textiles";
const INSTAGRAM_LINK = "https://www.instagram.com/tanish_textiles?utm_source=qr";

export default function Home() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#FAF8F5] flex flex-col">
      {/* Header — Staff pill on its own row, then centered logo below */}
      <header className="px-4 sm:px-6 pt-6">
        <div className="flex justify-end">
          <button
            data-testid="admin-link-btn"
            onClick={() => navigate("/admin/login")}
            className="inline-flex items-center gap-1.5 rounded-full border border-line bg-white/60 backdrop-blur px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] text-muted2 hover:text-maroon hover:border-maroon/40 transition-colors"
            aria-label="Staff login"
          >
            <Lock size={11} strokeWidth={1.7} />
            Staff
          </button>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="flex justify-center mt-6 sm:mt-8"
        >
          <Logo size="md" withMonogram={false} />
        </motion.div>
      </header>

      {/* Main welcome */}
      <main className="flex-1 flex items-center justify-center px-6 py-14 sm:py-20">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={{
            hidden: { opacity: 0 },
            visible: {
              opacity: 1,
              transition: { staggerChildren: 0.12, delayChildren: 0.1 },
            },
          }}
          className="w-full max-w-xl text-center"
        >
          <motion.h1
            data-testid="hero-heading"
            variants={{
              hidden: { opacity: 0, y: 12 },
              visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } },
            }}
            className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.08] text-ink"
          >
            Welcome to the
            <br />
            Tanish Textiles Family
          </motion.h1>

          <motion.div
            variants={{
              hidden: { opacity: 0 },
              visible: { opacity: 1, transition: { duration: 0.6, delay: 0.1 } },
            }}
            className="mx-auto mt-8 mb-8 h-px w-12 bg-maroon/60"
          />

          <motion.div
            variants={{
              hidden: { opacity: 0, y: 8 },
              visible: { opacity: 1, y: 0, transition: { duration: 0.7 } },
            }}
            className="space-y-4 text-[15px] sm:text-base text-muted2 leading-[1.75]"
          >
            <p>Thank you for placing your trust in us.</p>
            <p>We&rsquo;re delighted to be a part of your celebration.</p>
            <p>
              Before we prepare your outfit for dispatch, we&rsquo;d like to
              confirm a few delivery details to ensure it reaches you safely
              and on time.
            </p>
            <p className="italic text-ink/70">This will only take a minute.</p>
          </motion.div>

          <motion.div
            variants={{
              hidden: { opacity: 0, y: 10 },
              visible: { opacity: 1, y: 0, transition: { duration: 0.6, delay: 0.05 } },
            }}
            className="mt-12"
          >
            <button
              data-testid="continue-btn"
              onClick={() => navigate("/invoice")}
              className="btn-maroon w-full sm:w-auto sm:px-14 text-base group"
            >
              Continue
              <ArrowRight
                size={18}
                strokeWidth={1.6}
                className="transition-transform duration-300 group-hover:translate-x-1"
              />
            </button>
          </motion.div>

          <motion.p
            variants={{
              hidden: { opacity: 0 },
              visible: { opacity: 1, transition: { duration: 0.6, delay: 0.15 } },
            }}
            data-testid="trust-note"
            className="mt-6 inline-flex items-center gap-1.5 text-[11px] text-muted2 leading-relaxed"
          >
            <Lock size={12} strokeWidth={1.6} className="text-maroon/70" />
            Your information is secure and will only be used for preparing and
            delivering your order.
          </motion.p>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="border-t border-line/60 py-8 px-6" data-testid="site-footer">
        <div className="max-w-xl mx-auto text-center">
          <p className="overline text-[10px] mb-4">Need assistance?</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-8 text-sm text-ink">
            <a
              data-testid="footer-whatsapp"
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 hover:text-maroon transition-colors"
            >
              <WhatsAppIcon size={15} className="text-maroon" />
              <span>WhatsApp: {WHATSAPP_NUMBER}</span>
            </a>
            <span className="hidden sm:inline text-line">·</span>
            <a
              data-testid="footer-instagram"
              href={INSTAGRAM_LINK}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 hover:text-maroon transition-colors"
            >
              <Instagram size={15} strokeWidth={1.6} className="text-maroon" />
              <span>{INSTAGRAM_HANDLE}</span>
            </a>
          </div>
          <p className="mt-6 text-[10px] uppercase tracking-[0.28em] text-muted2">
            Tanish Textiles · Mumbai
          </p>
        </div>
      </footer>
    </div>
  );
}
